import { create } from 'zustand'
import type { NativeVersionUpdate } from '@/lib/nativeVersion'

/**
 * State behind the native update ladder. The decision logic itself is pure and lives in
 * lib/nativeUpdateTiers.ts; this file only remembers two facts per version — when we first
 * saw it, and until when the user silenced it.
 *
 * WHAT CHANGED AND WHY. The previous shape was a single `dismissedVersion: string` written
 * to localStorage forever. One tap on the X removed that release from the app's world
 * permanently — no expiry, no re-surface. Measured 2026-08-20, a release could go unnoticed
 * for a week for an entirely different reason (a stale manifest), and this store is the
 * second, independent way the same silence could happen: the user's own accidental tap.
 * Snooze expires; suppression does not, and that is the whole difference.
 */
const STORAGE_KEY = 'buildlog.nativeUpdateState'

/**
 * The pre-ladder key. Read once, for migration, then left alone.
 *
 * NOT deleted on migration, deliberately: removing it makes a downgrade to an older build
 * silently lose the user's dismissal and spam them. It costs one dead localStorage entry to
 * make the rollback path boring.
 */
const LEGACY_DISMISS_KEY = 'buildlog.dismissedNativeVersion'

type PersistedState = {
  snoozedVersion: string | null
  snoozedUntil: number | null
  firstSeenByVersion: Record<string, number>
}

type NativeVersionUpdateState = PersistedState & {
  availableUpdate: NativeVersionUpdate | null
  setAvailableUpdate: (update: NativeVersionUpdate | null) => void
  snooze: (version: string, durationMs: number) => void
}

const EMPTY: PersistedState = {
  snoozedVersion: null,
  snoozedUntil: null,
  firstSeenByVersion: {},
}

/**
 * Longest a stored snooze may sit in the future before we call it corrupt.
 *
 * The ladder's own longest snooze is 24h, so anything past a month did not come from this
 * code. It matters because the failure is silent and permanent in the WRONG direction: a
 * `snoozedUntil` of 1e30 hides the prompt effectively forever, which is precisely the
 * ignore-forever hole the ladder replaced — reintroduced through a corrupt storage value
 * instead of a tap.
 */
const MAX_SNOOZE_HORIZON_MS = 30 * 24 * 60 * 60 * 1000

/** A stored epoch is usable only if it is a real, finite, non-negative number. */
function usableTimestamp(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

/**
 * Drop unusable entries rather than trusting the cast.
 *
 * A non-numeric `firstSeenByVersion` value is the nastiest of these: it reaches
 * tierForAge(), every comparison against NaN is false, and that version is pinned at tier 0
 * FOREVER — escalation disabled with no error anywhere. Dropping the entry restarts that
 * version's clock, which is the safe direction: the user sees the quiet card now and the
 * ladder still climbs later.
 */
function sanitizeFirstSeen(value: unknown): Record<string, number> {
  if (typeof value !== 'object' || value === null) return {}

  const out: Record<string, number> = {}
  for (const [version, seenAt] of Object.entries(value as Record<string, unknown>)) {
    if (usableTimestamp(seenAt)) out[version] = seenAt
  }
  return out
}

/** A snooze we cannot trust is discarded, never extended: showing the prompt is the safe error. */
function sanitizeSnoozedUntil(value: unknown, now: number): number | null {
  if (!usableTimestamp(value)) return null
  return value > now + MAX_SNOOZE_HORIZON_MS ? null : value
}

function readPersisted(): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw)
      if (typeof parsed === 'object' && parsed !== null) {
        const state = parsed as Partial<PersistedState>
        return {
          snoozedVersion: typeof state.snoozedVersion === 'string' ? state.snoozedVersion : null,
          snoozedUntil: sanitizeSnoozedUntil(state.snoozedUntil, Date.now()),
          firstSeenByVersion: sanitizeFirstSeen(state.firstSeenByVersion),
        }
      }
    }

    // Migration. A legacy permanent dismissal becomes a snooze that has ALREADY expired, so
    // the version resurfaces exactly once and then follows the ladder. Carrying it forward as
    // an active snooze would preserve the bug we are removing; ignoring it entirely would
    // punish the user for a tap they made under the old rules by escalating immediately.
    const legacy = localStorage.getItem(LEGACY_DISMISS_KEY)
    if (typeof legacy === 'string' && legacy.length > 0) {
      return { snoozedVersion: legacy, snoozedUntil: 0, firstSeenByVersion: {} }
    }
  } catch {
    // Storage can be unavailable (private mode, quota); the ladder then lasts one session.
  }

  return { ...EMPTY, firstSeenByVersion: {} }
}

function persist(state: PersistedState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // Storage can be unavailable; snooze then lasts for this session only.
  }
}

export { MAX_SNOOZE_HORIZON_MS, sanitizeFirstSeen, sanitizeSnoozedUntil }

export const useNativeVersionUpdateStore = create<NativeVersionUpdateState>((set, get) => ({
  ...readPersisted(),
  availableUpdate: null,

  setAvailableUpdate: availableUpdate => {
    if (availableUpdate === null) {
      set({ availableUpdate: null })
      return
    }

    const { firstSeenByVersion } = get()

    // First observation wins. Re-stamping on every poll would reset the age on every app
    // launch and pin every user at tier 0 forever — the ladder would exist and never climb.
    if (firstSeenByVersion[availableUpdate.version] !== undefined) {
      set({ availableUpdate })
      return
    }

    const nextFirstSeen = { ...firstSeenByVersion, [availableUpdate.version]: Date.now() }
    const { snoozedVersion, snoozedUntil } = get()
    persist({ snoozedVersion, snoozedUntil, firstSeenByVersion: nextFirstSeen })
    set({ availableUpdate, firstSeenByVersion: nextFirstSeen })
  },

  snooze: (version, durationMs) => {
    const snoozedUntil = Date.now() + durationMs
    const { firstSeenByVersion } = get()
    persist({ snoozedVersion: version, snoozedUntil, firstSeenByVersion })
    set({ snoozedVersion: version, snoozedUntil })
  },
}))

export function resetNativeVersionUpdateStoreForTests(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(LEGACY_DISMISS_KEY)
  } catch {
    // Ignore unavailable storage in tests.
  }
  useNativeVersionUpdateStore.setState({ ...EMPTY, firstSeenByVersion: {}, availableUpdate: null })
}
